import { CoinBadgeV2 } from "@components/CoinBadgeV2";
import { CoinDropdownItem } from "@components/dropdown/CoinDropdownItem";
import { CustomDropdown } from "@components/dropdown/CustomDropdown";
import { useAccount } from "@contexts/AccountContext";
import { useCoins } from "@contexts/CoinsContext";
import { MsgExecuteContract } from "@goblinhunt/cosmes/client";
import useAlert from "@hooks/AlertHook";
import { errorPromise } from "@hooks/ApiHook";
import { useTxModal } from "@hooks/TxModalHook";
import { useWasmQuery } from "@hooks/WasmQueryHook";
import { useWasmX } from "@hooks/WasmXHook";
import { CoinResponse, CollectionResponse, NftResponse } from "baaanggg";
import React, { useEffect, useState } from "react";
import { AssetRequest, MessageDetail } from "wasmx";

export interface Props {
  nft: NftResponse;
  collection: CollectionResponse;
}

export function NftBidItemBox({ nft, collection }: Props) {
  const wasmx = useWasmX();
  const alert = useAlert();
  const txModal = useTxModal();
  const { address, wallet } = useAccount();
  const { coins, getCoin } = useCoins();

  const [selectedCoin, setSelectedCoin] = useState<CoinResponse>();
  const [amount, setAmount] = useState("");

  const { data: bidInfo, refresh: refreshBidInfo } = useWasmQuery(
    (wasmx) => {
      if (!collection || !address) return errorPromise();
      return wasmx.collection.query.bidByBuyer({
        contract: collection.collectionContract,
        data: {
          buyer: address,
        },
      });
    },
    [collection, address]
  );

  const onBid = async () => {
    try {
      const parsedAmount = Number.parseFloat(amount);

      if (isNaN(parsedAmount)) {
        alert.failed(
          `Bid price must be a number greater than 0, use '.' as decimal separator`
        );
        return;
      }

      if (parsedAmount <= 0) {
        alert.failed("Bid price must be greater than 0");
        return;
      }

      if (!selectedCoin) throw new Error("Please select coin or token");
      if (!wasmx || !address)
        throw new Error(
          "Cannot load chain information, try to refresh the page"
        );

      let asset: AssetRequest;
      const totalAmount = parsedAmount * Math.pow(10, selectedCoin.decimals);
      if (selectedCoin.isCw20)
        asset = {
          token: {
            address: selectedCoin.cw20Address!,
            amount: totalAmount.toString(),
          },
        };
      else
        asset = {
          coin: {
            denom: selectedCoin.denom!,
            amount: totalAmount.toString(),
          },
        };
      const bidMsg: MsgExecuteContract<any>[] = [];

      if (selectedCoin.isCw20) {
        console.log(asset.token?.address);
        bidMsg.push(
          new MsgExecuteContract({
            sender: address,
            contract: asset.token?.address ?? "",
            msg: {
              send: {
                contract: collection.collectionContract,
                amount: asset.token?.amount,
                msg: Buffer.from(
                  JSON.stringify({
                    bid: {
                      token_id: nft.tokenId,
                      buyer_address: address,
                    },
                  })
                ).toString("base64"),
              },
            },
            funds: [],
          })
        );
      } else {
        bidMsg.push(
          new MsgExecuteContract({
            contract: collection.collectionContract,
            sender: address,
            msg: {
              bid: {
                token_id: nft.tokenId,
                buyer_address: address,
              },
            },
            funds: [
              {
                amount: asset.coin?.amount.toString() ?? "0",
                denom: asset.coin?.denom ?? "",
              },
            ],
          })
        );
      }

      await txModal.show(bidMsg, `Bid NFT '${nft.name}'`, {
        onSuccess: async (tx) => {
          try {
            alert.loading("Sign Tx");
            await wallet!.pollTx(tx, {
              maxAttempts: 120_000,
              intervalSeconds: 15,
            });
          } catch (e) {
            // kalau mau, tampilkan warning tapi lanjut refresh
            console.warn("poll failed", e);
          }
          alert.success("Bid sent successfully");
          await refreshBidInfo();
        },
        onError: (er) => alert.failed(er.message),
      });
    } catch (e) {
      if (e instanceof Error) alert.failed(e.message);
      else console.error(e);
    }
  };

  const onWithdraw = async () => {
    try {
      if (!wasmx || !address)
        throw new Error(
          "Cannot load chain information, try to refresh the page"
        );

      const withdrawBid = new MsgExecuteContract({
        contract: collection.collectionContract,
        sender: address,
        msg: {
          withdraw_bid: {
            token_id: nft.tokenId,
          },
        },
        funds: [],
      });

      await txModal.show([withdrawBid], `Withdraw bid NFT '${nft.name}'`, {
        onSuccess: async (tx) => {
          try {
            alert.loading("Sign Tx");
            await wallet!.pollTx(tx, {
              maxAttempts: 120_000,
              intervalSeconds: 15,
            });
          } catch (e) {
            // kalau mau, tampilkan warning tapi lanjut refresh
            console.warn("poll failed", e);
          }
          alert.success("Withdraw bid sent successfully");
          await refreshBidInfo();
        },
        onError: (er) => alert.failed(er.message),
      });
    } catch (e) {
      if (e instanceof Error) alert.failed(e.message);
      else console.error(e);
    }
  };

  const bidExist = () => bidInfo?.bids.some((e) => e.token_id == nft.tokenId);

  return (
    <div className="rounded-2lg border border-jacarta-100 bg-white p-8 dark:border-gray-400 dark:bg-gray-900">
      {!bidExist() && (
        <div className="mb-6 flex flex-shrink-0 items-center">
          <div className="mr-2 flex w-1/2 items-center text-xs font-medium sm:text-sm">
            <CustomDropdown
              value={selectedCoin?.id}
              format={(coin) => coin.name}
              onSelected={(coin) => setSelectedCoin(coin)}
              builder={(coin, isSelected) => (
                <CoinDropdownItem coin={coin} isSelected={isSelected} />
              )}
              items={coins}
            />
          </div>
          <div className="mr-2 flex w-full items-center text-xs font-medium sm:text-sm">
            <input
              type="text"
              value={amount}
              onChange={({ target }) => setAmount(target.value)}
              placeholder="100"
              className="flex h-10 w-full items-center whitespace-nowrap border border-jacarta-100 bg-white p-3 text-white placeholder:text-gray-400 first:rounded-l-lg last:rounded-r-lg hover:ring-2 hover:ring-jacarta-500/10 focus:ring-jacarta-500 dark:border-gray-400 dark:bg-gray-900 sm:px-4 sm:py-2"
            />
          </div>
        </div>
      )}

      {bidExist() && (
        <div className="flex flex-wrap">
          {bidInfo!.bids
            .filter((e) => e.token_id == nft.tokenId)
            .map((e) => {
              const coin = getCoin(
                e.bid_price.coin?.denom ?? e.bid_price.token?.address ?? ""
              );

              return (
                <div className="mb-2.5 mr-2.5" key={coin?.id}>
                  <CoinBadgeV2
                    amount={Number(
                      e.bid_price.coin?.amount ?? e.bid_price.token?.amount
                    )}
                    coinId={coin?.id}
                  />
                </div>
              );
            })}
        </div>
      )}

      {bidExist() && (
        <button
          onClick={onWithdraw}
          className="inline-block w-full rounded-full bg-jacarta-500 px-8 py-3 text-center font-semibold text-white transition-all hover:bg-jacarta-600"
        >
          Withdraw Bid
        </button>
      )}

      {!bidExist() && (
        <button
          onClick={onBid}
          className="inline-block w-full rounded-full bg-jacarta-500 px-8 py-3 text-center font-semibold text-white transition-all hover:bg-jacarta-600"
        >
          Bid
        </button>
      )}
    </div>
  );
}
